import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";

/** Generic review list: items = [{key, title, subtitle, note, warn}] */
export const ReviewDialog = ({ open, onOpenChange, title, description, load, onApply, applyLabel, testId, empty }) => {
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setItems(null);
    load().then((list) => { setItems(list); setSel(Object.fromEntries(list.map((i) => [i.key, !i.warn]))); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const chosen = (items || []).filter((i) => sel[i.key]);
  const apply = async () => {
    setBusy(true);
    try { await onApply(chosen); onOpenChange(false); } catch (e) { toast.error(String(e?.message || e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] sm:max-w-2xl" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-700" />{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="thin-scroll max-h-[55vh] overflow-y-auto rounded-lg border border-slate-200">
          {!items && <div className="flex items-center gap-2 p-4 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Wird geladen …</div>}
          {items?.length === 0 && <div className="p-4 text-sm text-slate-600" data-testid={`${testId}-empty`}>{empty || "Keine Vorschläge vorhanden."}</div>}
          {items?.map((i) => (
            <label key={i.key} className="flex cursor-pointer items-start gap-3 border-b border-slate-100 px-3 py-2.5 last:border-0 hover:bg-slate-50" data-testid={`${testId}-item-${i.key}`}>
              <Checkbox checked={!!sel[i.key]} onCheckedChange={(v) => setSel({ ...sel, [i.key]: !!v })} className="mt-1" data-testid={`${testId}-check-${i.key}`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900">{i.title}</div>
                <div className="text-sm text-slate-600">{i.subtitle}</div>
                {i.note && <div className={`text-xs ${i.warn ? "text-amber-700" : "text-slate-500"}`}>{i.note}</div>}
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button disabled={busy || !chosen.length} onClick={apply} data-testid={`${testId}-apply-btn`} className="gap-1.5 bg-slate-900 hover:bg-slate-800">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{applyLabel} ({chosen.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
